import { FMT } from '../../../util/formatter-service';
import {
  DocsRole,
  LedgerRole,
  Precedence,
  RebuildScope,
  RoundAssignmentMode,
  RoundMatchMode,
} from '../../../data-models/round-creation-settings.data-model';

// Client-facing wording, lifted from the blessed settings reference. Reviewed in one place on purpose.

export interface ModeCopy {
  label: string;
  description: string;
}

export const MATCH_MODE_ORDER: RoundMatchMode[] = [
  RoundMatchMode.STAGE,
  RoundMatchMode.DATE,
  RoundMatchMode.STAGE_AND_DATE,
];

export function creationModeCopy(roundLabel: string): Record<RoundMatchMode, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  return {
    [RoundMatchMode.STAGE_AND_DATE]: {
      label: 'Both stage & date',
      description: `Must be the same stage and within the day window. A second Series B closing eight months later becomes its own ${lower}; B-1 and B-2 stay separate if they are outside the window.`,
    },
    [RoundMatchMode.STAGE]: {
      label: 'Just stage',
      description: `Same stage matches no matter how far apart the dates are. All Series B money ends up in one Series B ${lower}, whether it arrived in January or two years later. This is the setting that merges B-1 and B-2 into a single ${lower}.`,
    },
    [RoundMatchMode.DATE]: {
      label: 'Just date',
      description: `Only the date matters. Anything within the window joins the same ${lower}, even across stages, so a Series A closing and a bridge note two weeks apart merge into one. For clients who think of a ${lower} as a fundraising event rather than a share class.`,
    },
  };
}

export function assignmentModeCopy(roundLabel: string): Record<RoundMatchMode, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  return {
    [RoundMatchMode.STAGE_AND_DATE]: {
      label: 'Both stage & date',
      description: `Same stage and inside the assignment window. Matches today's behaviour.`,
    },
    [RoundMatchMode.STAGE]: {
      label: 'Just stage',
      description: `A leftover transaction joins any ${lower} of the same stage, regardless of date. Most forgiving: a late true-up always finds its ${lower}.`,
    },
    [RoundMatchMode.DATE]: {
      label: 'Just date',
      description: `The nearest ${lower} inside the window wins, stage ignored. Useful when the ledger's stage labels are unreliable.`,
    },
  };
}

export const ASSIGNMENT_MODE_ORDER: RoundAssignmentMode[] = [
  RoundAssignmentMode.MATCH_STAGE,
  RoundAssignmentMode.PREFERRED,
];

/** Which round wins when one window holds several securities. Distinct from the match mode above. */
export function securityAssignmentModeCopy(roundLabel: string): Record<RoundAssignmentMode, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    [RoundAssignmentMode.MATCH_STAGE]: {
      label: 'Always assign to matching stage',
      description: `Each transaction goes to the ${lower} matching its own stage — different stages in one window produce separate ${plural}.`,
    },
    [RoundAssignmentMode.PREFERRED]: {
      label: `Always assign to stage from 'Preferred' security`,
      description: `Transactions in the window group under the ${lower} whose stage comes from the Preferred security. Available when securities other than Preferred auto-create ${plural}.`,
    },
  };
}

/**
 * How the planned count compares with today, in one sentence. Shared by the impact strip and the
 * preview modal: they were near-copies of the same branch and sign logic, and the two had already
 * drifted on whether a change is worth colouring.
 */
export function deltaLabel(
  planned: number,
  today: number,
  copy: { matches: (count: string) => string; delta: (signed: string, count: string) => string }
): string {
  const delta = planned - today;
  const todayFormatted = FMT.format('integer', today);
  if (delta === 0) {
    return copy.matches(todayFormatted);
  }
  return copy.delta(`${delta > 0 ? '+' : ''}${FMT.format('integer', delta)}`, todayFormatted);
}

export function sectionCopy(roundLabel: string) {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    pageIntro: `Control how transactions from your data sources auto-create ${plural}, deduplicate closings, and assign transactions to stages.`,
    appliesTo: 'Applies to: all funds',
    banner: `Changes apply on the next data sync. Existing ${plural} are not modified retroactively — review results in Finance › ${roundLabel}s.`,
    reprocess: {
      label: `Reprocess historical ${plural}…`,
      badge: 'CS only',
      help: `Re-runs ${lower} creation over existing ledger data. Run only on client request.`,
    },
    impact: {
      pickCompany: 'Search a portfolio company',
      preview: 'Preview impact…',
      matches: (count: string) => `matches current behavior (${count})`,
      today: (count: string) => `today: ${count}`,
      underConfig: (company: string) => `${plural} for ${company} under this configuration`,
      noCompany: `Pick a company to see how many ${plural} these settings would give it.`,
      delta: (signed: string, count: string) => `${signed} vs current behavior (${count})`,
    },
    matching: {
      title: `${roundLabel} matching & dedupe`,
      modeLabel: `Create a ${lower} by`,
      windowLabel: `Date range window — ${lower} creation`,
      windowHelp: `How many days apart two transactions can be and still count as the same ${lower}. 60 is roughly one quarter of slack, which absorbs a raise that trickles in over a couple of months. Worked example: at 60 days the two Series A closings on 17 July and 26 October 2017, 101 days apart, stay separate; at 120 days they become one.`,
    },
    assignment: {
      title: 'Transaction assignment',
      securitiesLabel: 'When multiple securities fall in the same window',
      modeLabel: `Attach a transaction to a ${lower} by`,
      windowLabel: 'Date range window — transaction assignment',
      windowHelp: `Independent of the creation window. Wider than the creation window is the common useful setup: ${plural} are created strictly, late transfers still land. Much narrower means more transactions flagged unassigned.`,
    },
    grace: {
      label: 'Grace period before flagging unassigned',
      intro: `How long a transaction that found no ${lower} may keep trying before it is flagged. The clock starts at the first miss. A miss is often just early: the ${lower} frequently arrives later from a document sync or a later ledger batch.`,
      help: `0 flags immediately, which is today's behaviour. A short window such as 7 or 14 absorbs ordinary timing gaps. A quarter suits clients whose true-ups arrive at quarter end, at the cost of a genuine miss staying invisible for up to a quarter.`,
    },
    footer: {
      save: 'Save',
      cancel: 'Cancel',
      resetDefaults: 'Reset to defaults',
    },
    notSeeded: `This client has no saved ${lower} settings yet. Data loads stop until they are saved. Save once to seed them.`,
    savedGoForward: `Saved. New data follows these rules from now on; every ${lower} already decided stays as it is.`,
  };
}

export interface ScopeCopy {
  label: string;
  description: string;
}

export function scopeCopy(roundLabel: string): Record<RebuildScope, ScopeCopy> {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    [RebuildScope.GO_FORWARD]: {
      label: 'Going forward only',
      description: `Nothing existing is touched. The new rules apply to data arriving from now on, and to transactions that never found a ${lower}.`,
    },
    [RebuildScope.UNEDITED_SYSTEM]: {
      label: `Rebuild ${plural} nobody has edited`,
      description: `Rebuilds the ${plural} the system created and nobody touched, all the way back. Hand-edited ${plural} are kept and act as fixed points the rebuild groups around. A ${lower} with no history is kept too, and counted.`,
    },
    [RebuildScope.ALL]: {
      label: `Rebuild every ${lower}`,
      description: `Rebuilds everything, including the ${plural} the option above would have kept — hand-fixed ones, and ${plural} with no recorded history. Those are destroyed and rebuilt from the ledger; their history notes are carried across. You will be asked to confirm the exact number, which on older data is usually every ${lower} the company has.`,
    },
  };
}

// --- Sources, precedence, and what counts as a round. Copy follows the prototype. ---

export function ledgerRoleCopy(roundLabel: string): Record<LedgerRole, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    [LedgerRole.CREATE]: {
      label: `Create and attach`,
      description: `Ledger ingestion mints a new ${lower} when no match exists within the creation window, and attaches transactions to ${plural} that already exist.`,
    },
    [LedgerRole.TAG]: {
      label: 'Attach only, never create',
      description: `Ledger transactions only attach to existing ${plural} — they never create one. The right setting when documents are the source of truth for what a ${lower} is and the ledger just supplies the money.`,
    },
    [LedgerRole.IGNORE]: {
      label: `Leave ${plural} alone entirely`,
      description: `Round assignment is skipped during ledger loads: no creation, no attachment. Transactions are recorded as excluded, so the preview can explain an empty result rather than looking broken.`,
    },
  };
}

export function docsRoleCopy(roundLabel: string): Record<DocsRole, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    [DocsRole.ENRICH_ONLY]: {
      label: `Enrich existing ${plural} only`,
      description: `Extracted financings fill in raise, valuation and investors on ${plural} that already exist, but never create one. If no match exists yet the financing is held and retried on every sync — never dropped.`,
    },
    [DocsRole.CREATE]: {
      label: `Create ${plural}`,
      description: `Documents may create ${plural} themselves, one per financing, with no grouping. If the paperwork says seven financings, seven ${plural} exist.`,
    },
  };
}

export function precedenceCopy(roundLabel: string): Record<Precedence, ModeCopy> {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    [Precedence.LEDGER_LEADS]: {
      label: 'Ledger leads',
      description: `The ledger creates ${plural}; documents tag and enrich. Today's behaviour for every client.`,
    },
    [Precedence.DOCS_LEAD]: {
      label: 'Documents lead',
      description: `Documents create ${plural} first, since they usually arrive before the ledger; ledger transactions tag into them on their next load.`,
    },
  };
}

/** Plain-language names for the security kinds the API returns. */
export function securityTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    PREFERRED: 'Preferred',
    COMMON: 'Common',
    NOTE: 'Convertible note',
    SAFE: 'SAFE',
    WARRANT: 'Warrant',
    TOKEN: 'Token',
    UNIT: 'Unit',
    SECONDARY: 'Secondary',
    OTHER: 'Anything else',
  };
  return labels[type] ?? type;
}

export function sourcesCopy(roundLabel: string) {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    sources: {
      title: 'Data sources & precedence',
      ledgerTitle: 'Ledger',
      docsTitle: 'Deal docs / Aumni',
      precedenceTitle: 'When both sources are active',
      precedenceIntro: `Only matters when documents may create as well. It settles who wins, so the same ${lower} is not minted twice from two directions.`,
      inheritedLabel: 'Inherited.',
      inheritedHelp: `Nothing has been chosen here yet, so the client's existing setup still decides. Choosing a value makes it explicit and takes it off the old configuration.`,
      docsInheritedHelp: `Nothing has been chosen here yet, so the client's existing round provider still decides. Choosing a value makes it explicit — including choosing the value already shown.`,
      ambiguous: `This client's old configuration sets two contradictory switches at once, and it is being read as leaving ${plural} alone entirely. Choose a value here to make the intent deliberate.`,
      consolidatedTitle: 'Consolidated from Client Config:',
      consolidatedKeys: ['shouldCreateRounds', 'shouldSkipRoundAssignment', 'roundProvider'],
      consolidatedHelp: 'move out of the generic Client Config key/value list and are managed here.',
      relatedTitle: 'Related, and unchanged:',
      relatedKeys: ['dealDocAutoMappingEnabled', 'Aumni_Financing_Reconciliation'],
    },
    autoCreation: {
      title: `${roundLabel} auto-creation`,
      conversionsLabel: `A note or SAFE converting into shares creates a ${lower}`,
      conversionsHelp: `A conversion is a note or SAFE turning into shares — money in earlier, shares now. On, the conversion date mints a ${lower}, which is what happens today. Off, the converted money stays with the original security and no ${lower} is created.`,
      securitiesTitle: `Securities that auto-create ${plural}`,
      securitiesHelp: `Anything not selected can still attach to an existing ${lower} within the assignment window; it just cannot start one. A common example: warrants and options attach but never define a ${lower}, so option grants stop creating phantom ${plural}.`,
      securitiesSelectAll: 'Select all',
    },
  };
}

/**
 * The engine detail behind each setting, shown on hover. Facts about how the code actually
 * decides, not a restatement of the label.
 */
export function settingTips(roundLabel: string) {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    docsRole: `Documents already carry ${lower} names, so they can name one exactly. They cannot group: one financing becomes one ${lower}, with no window and no merging.`,
    securityTypes: `Stage is derived from the position text, not a securities table. Positions containing "secondary", "common" or "unit" carry name overrides and no stage link, so they group separately from priced stages.`,
    matchBy: `Defines the identity of a ${lower}: what has to match before two transactions land in the same one. Today's ledger matcher uses the stage code plus a 60-day window, and falls back to nearest-by-date for a security it cannot decode.`,
    creationWindow: `A ${lower}-creating transaction dated outside this window from an existing ${lower} starts a new one. Hard-coded today: 60 days on the ledger, 90 on document and Aumni matching.`,
    assignmentMode: `Only decides anything when securities beyond Preferred may create. It settles which stage wins when transactions in different securities land inside one window.`,
    assignmentWindow: `A transaction that cannot create a ${lower} attaches to the nearest eligible one inside this window. Outside it, it surfaces as unassigned for manual review.`,
    grace: `The clock starts at the first miss and is cleared the moment the transaction finds a ${lower}. It does not delay assignment, only the point at which a miss is reported.`,
    conversions: `Today this is hard-coded: an Investment - Restructure In qualifies only when paired with a same-day note-like Restructure Out. Off, the converted money stays on the original security and mints no ${lower}.`,
    ledgerRole: `Replaces two client-config booleans that had four reachable states for these three: both set at once read as leaving ${plural} alone.`,
  };
}

/** Why a control is currently inert, said in place rather than by hiding it silently. */
export function condNotes(roundLabel: string) {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    assignmentMode: `Available when securities other than Preferred auto-create ${plural}.`,
    windowUnusedUnderStage: `Not used while ${plural} are created by stage alone.`,
  };
}

/** The config key each setting maps to, shown as a keychip so CS can name it on a call. */
export const CONFIG_KEYS = {
  ledgerRole: 'shouldCreateRounds',
  docsRole: 'roundProvider',
  conversions: 'roundCreation.conversionsCreateRounds',
  securityTypes: 'roundCreation.securityTypes',
  matchBy: 'roundCreation.matchBy',
  creationWindow: 'roundCreation.creationWindowDays',
  assignmentMode: 'roundCreation.assignmentMode',
  assignmentWindow: 'roundCreation.assignmentWindowDays',
  grace: 'roundCreation.unassignedGraceDays',
} as const;
